// @ts-ignore
import { defineAsyncComponent, h } from "vue";
import { AssetStore } from "../../../stores/assetStore";
import { UserStore } from "../../../stores/userStore";
import { SessionStore } from "../../../stores/sessionStore";
import { FlixlatinoApiClient } from "../../../services/FlixlatinoApiClient";

const isMobileDevice = () =>
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  window.innerWidth < 768;

const AssetPlayerAppView = defineAsyncComponent(() =>
  import(
    /*webpackChunkName: "AssetPlayerAppView"*/ "../../../views/app/AssetPlayerApp.vue"
  )
);

const AssetMobilePlayerAppView = defineAsyncComponent(() =>
  import(
    /*webpackChunkName: "AssetMobilePlayerAppView"*/ "../../../views/app/AssetMobilePlayerApp.vue"
  )
);

const PlayerGeoFenceView = defineAsyncComponent(() =>
  import(
    /*webpackChunkName: "PlayerGeoFenceView"*/ "../../../views/app/player/PlayerGeoFence.vue"
  )
);

const PlayRouteView = {
  name: "PlayRouteView",
  props: ["slug", "type", "season", "episode"],
  setup(props: any) {
    const sessionStore = SessionStore();

    return () =>
      h(
        sessionStore.getFenceAccess === false
          ? PlayerGeoFenceView
          : isMobileDevice()
          ? AssetMobilePlayerAppView
          : AssetPlayerAppView,
        props
      );
  },
};

const getAssetExperience = (asset: any) =>
  String(asset?.experience || "").trim().toLowerCase();

const canGuestPlayAsset = (asset: any) => {
  if (getAssetExperience(asset) !== "avod") {
    return false;
  }

  if (!!asset?.urlplay || !!asset?.current?.urlplay) {
    return true;
  }

  return Array.isArray(asset?.seasons)
    ? asset.seasons.some((season: any) =>
        Array.isArray(season?.episodes)
          ? season.episodes.some((episode: any) => !!episode?.urlplay)
          : false
      )
    : false;
};

const isPlayableValue = (value: any) =>
  value === true || value === 1 || value === "1" || value === "true";

const isExplicitlyNotPlayable = (value: any) =>
  value === false || value === 0 || value === "0" || value === "false";

const hasPlayableStream = (asset: any) => {
  if (!!asset?.urlplay || !!asset?.current?.urlplay) {
    return true;
  }

  if (isPlayableValue(asset?.current?.is_playable)) {
    return true;
  }

  return Array.isArray(asset?.seasons)
    ? asset.seasons.some((season: any) =>
        Array.isArray(season?.episodes)
          ? season.episodes.some(
              (episode: any) =>
                !!episode?.urlplay || isPlayableValue(episode?.is_playable)
            )
          : false
      )
    : false;
};

const isTopLevelTitlePlayRoute = (to: any) =>
  ["movie-play", "series-play"].includes(String(to?.name || ""));

const isEpisodePlayRoute = (to: any) =>
  String(to?.name || "") === "series-play-episode";

const getTitlePagePath = (to: any) =>
  String(to?.path || "").replace(/\/play\/?$/, "");

const getEpisodeTitlePagePath = (to: any) => {
  const path = String(to?.path || "");
  return path.replace(
    /\/(?:season|temporada)-[^/]+\/(?:episode|episodio)-[^/]+\/play\/?$/,
    ""
  );
};

const canPlayAsset = (asset: any, userStore: any) => {
  if (!asset) {
    return false;
  }

  const experience = getAssetExperience(asset);

  if (experience === "avod") {
    return userStore.isLoggedIn || canGuestPlayAsset(asset);
  }

  if (!userStore.isLoggedIn) {
    return false;
  }

  if (isExplicitlyNotPlayable(asset?.is_playable)) {
    return false;
  }

  if (isPlayableValue(asset?.is_playable) || hasPlayableStream(asset)) {
    return true;
  }

  return !!(userStore.isPaidUser || userStore.trialEntitlement);
};

const findRouteEpisode = (asset: any, season: string, episode: string) => {
  if (!asset) {
    return null;
  }

  const selectedSeason = String(season || "");
  const selectedEpisode = String(episode || "");

  if (
    asset?.current &&
    String(asset.current?.season ?? "") === selectedSeason &&
    String(asset.current?.episode ?? "") === selectedEpisode
  ) {
    return asset.current;
  }

  for (const seasonItem of Array.isArray(asset?.seasons) ? asset.seasons : []) {
    for (const episodeItem of Array.isArray(seasonItem?.episodes)
      ? seasonItem.episodes
      : []) {
      if (
        String(episodeItem?.season ?? seasonItem?.season ?? "") ===
          selectedSeason &&
        String(episodeItem?.episode ?? "") === selectedEpisode
      ) {
        return episodeItem;
      }
    }
  }

  return null;
};

const canPlayEpisode = (asset: any, episode: any, userStore: any) => {
  if (!asset || !episode) {
    return false;
  }

  const hasPlayableSignal =
    isPlayableValue(episode?.is_playable) ||
    !!episode?.urlplay ||
    !!episode?.src;

  if (isExplicitlyNotPlayable(episode?.is_playable) || !hasPlayableSignal) {
    return false;
  }

  const plan = String(userStore.getPlan || "").toLowerCase();

  if (plan === "premium") {
    return true;
  }

  if (isPlayableValue(episode?.is_upcoming)) {
    return false;
  }

  if (isPlayableValue(episode?.free)) {
    return true;
  }

  return userStore.isLoggedIn && plan === "basic";
};

const getRouteAsset = async (slug: string, lang: string | null = null) => {
  const assetStore = AssetStore();
  const apiClient = new FlixlatinoApiClient();
  let cachedAsset = assetStore.getAssetBySlug(slug);

  if (!cachedAsset) {
    await apiClient.getLoggedOutInitData(lang).catch(() => null);
    cachedAsset = assetStore.getAssetBySlug(slug);
  }

  if (cachedAsset?.id) {
    await apiClient.getAssetBySlug(String(cachedAsset.id), lang).catch(() => null);
    return assetStore.getAssetBySlug(slug) || assetStore.getAssetData(String(cachedAsset.id));
  }

  await apiClient.getAssetBySlug(slug, lang).catch(() => null);
  return assetStore.getAssetBySlug(slug);
};

const checkUserEntitlement = async (to: any, from: any, next: any) => {
  const userStore = UserStore();
  const slug = String(to?.params?.slug || "");

  if (isEpisodePlayRoute(to)) {
    const assetPage = getEpisodeTitlePagePath(to);
    if (!slug) {
      next({ path: assetPage });
      return;
    }

    const asset = await getRouteAsset(slug, to?.params?.locale || null);
    const episode = findRouteEpisode(
      asset,
      String(to?.params?.season || ""),
      String(to?.params?.episode || "")
    );

    if (canPlayEpisode(asset, episode, userStore)) {
      next();
      return;
    }

    next({ path: assetPage, query: { upconvert: "lockedEpisode" } });
    return;
  }

  if (!isTopLevelTitlePlayRoute(to)) {
    next();
    return;
  }
  const assetPage = getTitlePagePath(to);

  if (!slug) {
    next({ path: assetPage });
    return;
  }

  const asset = await getRouteAsset(slug);
  if (canPlayAsset(asset, userStore)) {
    next();
    return;
  }

  next({ path: assetPage });
};
export const moviePlay = {
  path: "movie/:slug/play",
  name: "movie-play",
  alias: [
    "pelicula/:slug/play/",
    "movies/:slug/play/",
    "peliculas/:slug/play/",
  ],
  props: (route: { params: { slug: any } }) => ({
    slug: route.params.slug,
    type: "movie",
  }),
  component: PlayRouteView,
  beforeEnter: checkUserEntitlement,
  meta: {
    isPlayer: true,
    requiresAuth: false,
    requiresEntitlement: false,
    isAmbiguous: true,
    entrypoint: true,
    lang: "en",
  },
};

export const seriesPlay = {
  path: "series/:slug/play",
  name: "series-play",
  alias: "serie/:slug/play/",
  props: (route: { params: { slug: any } }) => ({
    slug: route.params.slug,
    type: "series",
  }),
  component: PlayRouteView,
  beforeEnter: checkUserEntitlement,
  meta: {
    isPlayer: true,
    requiresAuth: false,
    requiresEntitlement: false,
    isAmbiguous: true,
  },
};

export const moviePlayTrailer = {
  path: "movie/:slug/play/trailer",
  name: "movie-play-trailer",
  alias: [
    "peliculas/:slug/play/trailer",
    "pelicula/:slug/play/trailer",
    "movies/:slug/play/trailer",
    "movie/:slug/play/trailer",
  ],
  props: (route: { params: { slug: any } }) => ({
    slug: route.params.slug,
    type: "trailer",
  }),
  component: PlayRouteView,
  meta: {
    isPlayer: true,
    requiresAuth: false,
    requiresEntitlement: false,
    isAmbiguous: true,
  },
};

export const seriesPlayTrailer = {
  path: "series/:slug/play/trailer",
  name: "series-play-trailer",
  alias: ["serie/:slug/play/trailer"],
  props: (route: { params: { slug: any } }) => ({
    slug: route.params.slug,
    type: "trailer",
  }),
  component: PlayRouteView,
  meta: {
    isPlayer: true,
    requiresAuth: false,
    requiresEntitlement: false,
    isAmbiguous: true,
  },
};

export const seriesPlayEpisode = {
  path: "series/:slug/season-:season/episode-:episode/play",
  name: "series-play-episode",
  alias: [
    "serie/:slug/season-:season/episode-:episode/play",
    "serie/:slug/temporada-:season/episodio-:episode/play",
    "series/:slug/temporada-:season/episodio-:episode/play",
  ],
  props: (route: {
    params: { slug: string; episode: string; season: string; type: "series" };
  }) => ({
    slug: route.params.slug,
    episode: route.params.episode,
    season: route.params.season,
    type: "series",
  }),
  component: PlayRouteView,
  beforeEnter: checkUserEntitlement,
  meta: {
    isPlayer: true,
    requiresAuth: false,
    requiresEntitlement: false,
    isAmbiguous: true,
  },
};
